import { useCallback, useEffect, useState } from 'react';

import { GraphQLError } from 'graphql';

// Generic type for a graphql client
export declare class GraphQLClientError extends Error {
  message: string;
  graphQLErrors: ReadonlyArray<GraphQLError>;
}
export type GraphqlClientResponse<TData> = {
  data: TData | undefined;
  error: GraphQLClientError | Error | undefined;
};

export type GraphQLClient = {
  query<TData, TVars>(query: string, vars: TVars): Promise<GraphqlClientResponse<TData>>;
};

export const useGraphqlQuery = <TData, TVars>(
  client: GraphQLClient,
  query: string,
  vars: TVars,
): GraphqlClientResponse<TData> & { loading: boolean; refresh: () => void } => {
  const queryFn = useCallback(
    () => client.query<TData, TVars>(query, vars),
    // this is just to fix the exhaustive deps false positive, it does use all of
    // the correct deps, query and vars are just in JSON.stringify
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, JSON.stringify({ query, vars })],
  );
  const [runQuery, setRunQuery] = useState(true);
  const [fetchState, setFetchState] = useState<GraphqlClientResponse<TData> & { loading: boolean }>(
    {
      loading: true,
      error: undefined,
      data: undefined,
    },
  );
  const refreshFn = useCallback(() => setRunQuery(true), []);
  useEffect(() => {
    if (!runQuery) {
      return;
    }

    let unmounted = false;
    async function execute() {
      try {
        const result = await queryFn();
        if (!unmounted) {
          setFetchState({ loading: false, data: result.data, error: result.error });
        }
      } catch (e) {
        if (!unmounted) {
          setFetchState({ loading: false, data: undefined, error: e as Error });
        }
      } finally {
        if (!unmounted) {
          setRunQuery(false);
        }
      }
    }
    execute();

    // Check unmounted before changing state ensures that
    // 1) If the entire component unmounted, we don't change state.
    // 2) If runQuery or queryFn change, we don't mix up state between different queries.
    return () => {
      unmounted = true;
    };
  }, [runQuery, queryFn]);
  return {
    ...fetchState,
    refresh: refreshFn,
  };
};
